import type { ThreadHistoryAdapter } from '@assistant-ui/react';

/** The guest's messages no successful answer has covered yet: the server may still block them as
 *  personal data, so they stay out of stored history and the thread title. Kept in memory only,
 *  which is enough: a message that is never stored is gone after a reload. */
const uncheckedIds = new Set<string>();

export function markUnchecked(messageId: string): void {
  uncheckedIds.add(messageId);
}

export function markChecked(messageIds: Iterable<string>): void {
  for (const messageId of messageIds) {
    uncheckedIds.delete(messageId);
  }
}

export function isUnchecked(messageId: string): boolean {
  return uncheckedIds.has(messageId);
}

export class UncheckedMessageError extends Error {
  public constructor(messageId: string) {
    super(`Message ${messageId} is not stored until the server accepts it`);
    this.name = 'UncheckedMessageError';
  }
}

function assertChecked(messageId: string): void {
  if (isUnchecked(messageId)) {
    throw new UncheckedMessageError(messageId);
  }
}

/**
 * History that refuses to store an unchecked message. It throws rather than skipping: the chat's
 * history sync then stops that pass before recording it, and stores the message, and the replies
 * after it, on the next run once the server has accepted it. A skip would be recorded as stored.
 */
export function withCheckedHistory(history: ThreadHistoryAdapter): ThreadHistoryAdapter {
  const withFormat = history.withFormat?.bind(history);
  return {
    load: () => history.load(),
    append: async item => {
      assertChecked(item.message.id);
      return history.append(item);
    },
    withFormat:
      withFormat &&
      (formatAdapter => {
        const formatted = withFormat(formatAdapter);
        const update = formatted.update?.bind(formatted);
        return {
          ...formatted,
          append: async item => {
            assertChecked(formatAdapter.getId(item.message));
            return formatted.append(item);
          },
          update:
            update &&
            (async (item, localMessageId) => {
              assertChecked(formatAdapter.getId(item.message));
              return update(item, localMessageId);
            }),
        };
      }),
  };
}
